import { useHomeCurrency, useMarketDataSnapshot, useOwnOrders } from '@src/hooks';
import { ORDER_SIDES } from '@src/tokens/order';
import Big from 'big.js';
import { isEqual } from 'lodash-es';
import { memo, useCallback, useState, type MouseEvent } from 'react';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { getSizeSpread, getSpreadDisplayValue } from './utils';

import { OwnOrders } from '@src/components/Ladder/OwnOrders';
import type { ClickToTradePreviewOrder } from '@src/components/MarketDataCard/types';
import { useIsPricingModeBPS } from '@src/hooks/useIsPricingModeBPS';
import type { OwnOrderResult } from '@src/hooks/useOwnOrders';
import { useDisplaySettings } from '@src/providers/AppConfigProvider';
import { getExpectedFillPriceAndSize } from '@src/utils/getExpectedFillPriceAndSize';
import type { OrderIntent } from '@talos/kyoko';
import {
  AggressorFlagEnum,
  InlineFormattedNumber,
  MixpanelEventSource,
  NoDataMessage,
  SIZE,
  SideEnum,
  SpreadUnit,
  UnifiedLiquidityEnum,
  getSpreadUnit,
  useEstimatedTotal,
  useObservableRef,
  useObservableValue,
  type MarketDataCardConfig,
  type MarketDataSnapshot,
  type Security,
} from '@talos/kyoko';

import { getMarketDataLevelPrices } from '@src/utils/getMarketDataLevelPrice';
import { useGetOrderCurrency } from '../OMS/useGetOrderCurrency';
import {
  BidBucket,
  BidsSideCell,
  HeaderRow,
  LadderBg,
  OfferBucket,
  OffersSideCell,
  Price,
  PxHeaderCell,
  QtyHeaderCell,
  Row,
  SpreadDisplay,
  SpreadHeaderCell,
  TotalPopup,
  Wrapper,
} from './styles';
import type { IOnClickPriceArgs, OnClickPriceFn } from './types';

export interface VolumeLadderProps {
  config: MarketDataCardConfig;
  security: Security;
  isReadOnly: boolean;
  activeOrder: ClickToTradePreviewOrder | undefined;
  onClickPrice: (
    args: IOnClickPriceArgs & { expectedFillPrice?: string; expectedFillQty?: string },
    index: number
  ) => any;
  onMouseLeavePrice: () => any;
  onSaveCard: (config: MarketDataCardConfig) => any;
  marketDataSnapshotThrottle?: string;
}

interface VolumeRow {
  size: string;
  minimumSize: string;
  bidDisplay?: string;
  bidPrice?: string;
  offerDisplay?: string;
  offerPrice?: string;
  sizeSpread: number;
  levelBids: OwnOrderResult[];
  levelOffers: OwnOrderResult[];
}

export const VolumeLadder = memo(function VolumeLadder({
  config,
  security,
  isReadOnly,
  activeOrder,
  marketDataSnapshotThrottle,
  onClickPrice,
  onMouseLeavePrice,
  onSaveCard,
}: VolumeLadderProps) {
  const { homeCurrency, showTotalEstimates } = useDisplaySettings();
  const isPricingModeBPS = useIsPricingModeBPS(security);
  const spreadUnit = getSpreadUnit(security, isPricingModeBPS);

  const { id, selectedMarkets, sizeBuckets, selectedMarketAccounts, unifiedLiquidity } = config;
  const { Symbol, DefaultPriceIncrement } = security;

  const { ownBids, ownOffers } = useOwnOrders({
    symbol: Symbol,
    markets: selectedMarkets,
    marketAccounts: selectedMarketAccounts,
    includeUnifiedLiquidity: unifiedLiquidity === UnifiedLiquidityEnum.Enabled,
  });

  const { marketDataSnapshots, marketDataErrors, isLoading } = useMarketDataSnapshot({
    markets: selectedMarkets,
    marketAccounts: selectedMarketAccounts,
    priceIncrement: DefaultPriceIncrement,
    sizeBuckets,
    symbol: Symbol,
    tag: `${id}/${SIZE}`,
    currency: undefined,
    baseCurrency: undefined,
    depth: undefined,
    includeTradeDirection: undefined,
    quantity: undefined,
    unifiedLiquidity,
    throttle: marketDataSnapshotThrottle,
  });

  const { getOrderCurrency } = useGetOrderCurrency();

  const marketDataSnapshotRef = useObservableRef<MarketDataSnapshot>(() => marketDataSnapshots, [marketDataSnapshots]);
  const handleClickPrice: VolumeLadderRowProps['onClickPrice'] = useCallback(
    (values, index) => {
      const orderCurrency = getOrderCurrency(security, values.side);
      const { expectedFillQty, expectedFillPrice } = getExpectedFillPriceAndSize(
        security,
        orderCurrency,
        marketDataSnapshotRef.current,
        values.side,
        values.orderQty
      );
      onClickPrice(
        {
          ...values,
          expectedFillPrice,
          expectedFillQty,
        },
        index
      );
    },
    [marketDataSnapshotRef, onClickPrice, security, getOrderCurrency]
  );

  const marketDataError = useObservableValue(() => marketDataErrors, [marketDataErrors]);

  const { rows, deepBids, deepOffers } = useObservableValue(
    () =>
      combineLatest<[MarketDataSnapshot, OwnOrderResult[], OwnOrderResult[]]>([
        marketDataSnapshots,
        ownBids,
        ownOffers,
      ]).pipe(
        map(([snapshot, ownBids, ownOffers]) => {
          const usedBids: Record<string, boolean> = {};
          const usedOffers: Record<string, boolean> = {};

          const sizeSpread = getSizeSpread(snapshot);

          const rows: VolumeRow[] = sizeBuckets.map((size, index) => {
            const minimumSize = index === 0 ? security.MinimumSize : sizeBuckets[index - 1];

            const { bidPrice, offerPrice } = getMarketDataLevelPrices({ snapshot, index, isPricingModeBPS });

            const levelBids: OwnOrderResult[] = [];
            for (const o of ownBids) {
              const key = `${o.orderID}${o.market}`;
              if (bidPrice && !usedBids[key] && Big(o.leavesQty ?? 0).lte(size) && Big(o.price ?? 0).gte(bidPrice)) {
                levelBids.push(o);
                usedBids[key] = true;
              }
            }

            const levelOffers: OwnOrderResult[] = [];
            for (const o of ownOffers) {
              const key = `${o.orderID}${o.market}`;
              if (
                offerPrice &&
                !usedOffers[key] &&
                Big(o.leavesQty ?? 0).lte(size) &&
                Big(o.price ?? 0).lte(offerPrice)
              ) {
                levelOffers.push(o);
                usedOffers[key] = true;
              }
            }

            return {
              size,
              minimumSize,
              bidDisplay: bidPrice,
              bidPrice,
              offerDisplay: offerPrice,
              offerPrice,
              sizeSpread: sizeSpread[index],
              levelBids,
              levelOffers,
            };
          });

          const deepBids = ownBids.filter(o => !usedBids[`${o.orderID}${o.market}`]);
          const deepOffers = ownOffers.filter(o => !usedOffers[`${o.orderID}${o.market}`]);

          return { rows, deepBids, deepOffers };
        }),
        distinctUntilChanged((p, n) => isEqual(p, n))
      ),
    [sizeBuckets, marketDataSnapshots, ownBids, ownOffers, security.MinimumSize, isPricingModeBPS],
    { rows: [], deepBids: [], deepOffers: [] }
  );

  const handleUpdateSize = useCallback(
    (index: number, newSize: string) => {
      const newSizeBuckets = [...sizeBuckets.slice(0, index), newSize, ...sizeBuckets.slice(index + 1)];

      if (index > 0 && parseFloat(sizeBuckets[index - 1]) > parseFloat(newSize)) {
        return;
      }

      const newConfig = {
        ...config,
        sizeBuckets: newSizeBuckets.map((bucketSize, bucketIndex) => {
          if (bucketIndex > index && parseFloat(bucketSize) < parseFloat(newSize)) {
            return newSize;
          }
          return bucketSize;
        }),
      };
      onSaveCard(newConfig);
    },
    [config, onSaveCard, sizeBuckets]
  );

  if (rows.length === 0 || marketDataError) {
    if (marketDataError) {
      return <NoDataMessage>{marketDataError.msg}</NoDataMessage>;
    }
    if (!isLoading) {
      return <NoDataMessage>No data available</NoDataMessage>;
    }
    return null;
  }

  return (
    <Wrapper>
      <VolumeLadderHeader differenceUnit={spreadUnit} />
      {rows.map((row, index) => {
        return (
          <VolumeLadderRow
            key={index}
            index={index}
            security={security}
            row={row}
            isReadOnly={isReadOnly}
            homeCurrency={homeCurrency}
            activeOrder={activeOrder}
            showTotalEstimates={showTotalEstimates}
            onUpdateSize={handleUpdateSize}
            onClickPrice={handleClickPrice}
            onMouseLeavePrice={onMouseLeavePrice}
          />
        );
      })}
      {/* We can't compare BPS on parent order with cash on market orders so hide if in BPS pricing mode */}
      {!isPricingModeBPS && (
        <Row>
          {deepBids.length > 0 && <OwnOrders isBid ownOrders={deepBids} />}
          {deepOffers.length > 0 && <OwnOrders isOffer ownOrders={deepOffers} />}
        </Row>
      )}
    </Wrapper>
  );
});

const VolumeLadderHeader = memo(({ differenceUnit }: { differenceUnit: SpreadUnit }) => (
  <HeaderRow>
    <QtyHeaderCell isLeftAligned>QTY</QtyHeaderCell>
    <PxHeaderCell isLeftAligned>LMT PX</PxHeaderCell>
    <SpreadHeaderCell>
      <div data-testid="volume-ladder-spread-header">{`Spread${
        differenceUnit === SpreadUnit.PRICE ? '' : ' (BPS)'
      }`}</div>
    </SpreadHeaderCell>
    <PxHeaderCell isRightAligned>LMT PX</PxHeaderCell>
    <QtyHeaderCell isRightAligned>QTY</QtyHeaderCell>
  </HeaderRow>
));

export interface VolumeLadderRowProps {
  index: number;
  security: Security;
  row: VolumeRow;
  isReadOnly: boolean;
  homeCurrency: string;
  activeOrder: ClickToTradePreviewOrder | undefined;
  showTotalEstimates: boolean;
  onUpdateSize: (index: number, newSize: string) => void;
  onClickPrice: OnClickPriceFn;
  onMouseLeavePrice: () => void;
}

const VolumeLadderRow = memo(
  function VolumeLadderRow({
    index,
    security,
    row,
    isReadOnly,
    homeCurrency,
    activeOrder,
    showTotalEstimates,
    onUpdateSize,
    onClickPrice,
    onMouseLeavePrice,
  }: VolumeLadderRowProps) {
    const { size, minimumSize, bidDisplay, bidPrice, offerDisplay, offerPrice, sizeSpread, levelBids, levelOffers } =
      row;

    const [hoveredSide, setHoveredSide] = useState<string | undefined>();
    const isBidActive =
      activeOrder?.source === MixpanelEventSource.VolumeLadder &&
      activeOrder.sourceIndex === index &&
      activeOrder.side === ORDER_SIDES.SELL;
    const isOfferActive =
      activeOrder?.source === MixpanelEventSource.VolumeLadder &&
      activeOrder.sourceIndex === index &&
      activeOrder.side === ORDER_SIDES.BUY;

    const bidTotal =
      useEstimatedTotal({
        security: security,
        quantity: size,
        quantityCurrency: security.PositionCurrency,
        price: bidDisplay,
      }) || '0';

    const offerTotal =
      useEstimatedTotal({
        security: security,
        quantity: size,
        quantityCurrency: security.PositionCurrency,
        price: offerDisplay,
      }) || '0';

    const handleHoverRow = useCallback((side: string) => {
      setHoveredSide(side);
    }, []);

    const handleMouseLeave = useCallback(() => {
      onMouseLeavePrice();
      setHoveredSide(undefined);
    }, [onMouseLeavePrice]);

    const shouldRowHighlight = Boolean(
      showTotalEstimates &&
      ((bidTotal && hoveredSide === ORDER_SIDES.SELL) || (offerTotal && hoveredSide === ORDER_SIDES.BUY))
    );
    const showBidTotal = Boolean(bidTotal && shouldRowHighlight && hoveredSide === ORDER_SIDES.SELL);
    const showOfferTotal = Boolean(offerTotal && shouldRowHighlight && hoveredSide === ORDER_SIDES.BUY);

    const bidCell = {
      bidDisplay,
      bidPrice,
      showOfferTotal,
      offerTotal,
      homeCurrency,
      size,
      minimumSize,
      sizeSpread,
      isReadOnly,
      isActive: isBidActive,
    };

    const offerCell = {
      offerDisplay,
      offerPrice,
      showBidTotal,
      bidTotal,
      homeCurrency,
      size,
      minimumSize,
      sizeSpread,
      isReadOnly,
      isActive: isOfferActive,
    };

    const isPricingModeBPS = useIsPricingModeBPS(security);
    const differenceUnit = getSpreadUnit(security, isPricingModeBPS);

    return (
      <Row
        showTotalEstimates={shouldRowHighlight}
        activeSide={
          activeOrder?.source === MixpanelEventSource.VolumeLadder &&
          activeOrder.sourceIndex === index &&
          activeOrder.side
        }
      >
        <BidSide
          index={index}
          security={security}
          cell={bidCell}
          onUpdateSize={onUpdateSize}
          onMouseHoverPrice={handleHoverRow}
          onMouseLeavePrice={handleMouseLeave}
          onClickPrice={onClickPrice}
        />
        {bidPrice && offerPrice && (
          <SpreadDisplay data-testid={`volume-ladder-spread-${index}`}>
            <p>{getSpreadDisplayValue(bidPrice, offerPrice, differenceUnit, isPricingModeBPS)}</p>
          </SpreadDisplay>
        )}
        <OfferSide
          index={index}
          security={security}
          cell={offerCell}
          onUpdateSize={onUpdateSize}
          onMouseHoverPrice={handleHoverRow}
          onMouseLeavePrice={handleMouseLeave}
          onClickPrice={onClickPrice}
        />

        {!isPricingModeBPS && levelBids.length > 0 && <OwnOrders isBid ownOrders={levelBids} />}
        {!isPricingModeBPS && levelOffers.length > 0 && <OwnOrders isOffer ownOrders={levelOffers} />}
      </Row>
    );
  },
  (p, n) => isEqual(p, n)
);

interface BidCell {
  bidDisplay?: string;
  bidPrice?: string;
  showOfferTotal: boolean | string;
  offerTotal: string;
  homeCurrency: string;
  size: string;
  minimumSize: string;
  sizeSpread: number;
  isReadOnly: boolean;
  isActive: boolean;
}

interface BidSideProps {
  index: number;
  security: Security;
  cell: BidCell;
  onUpdateSize: (index: number, newSize: string) => void;
  onMouseHoverPrice: (side: string) => void;
  onMouseLeavePrice: () => void;
  onClickPrice: (args: IOnClickPriceArgs, index: number) => any;
}

const BidSide = memo(
  function BidSide({
    index,
    security,
    cell,
    onUpdateSize,
    onMouseHoverPrice,
    onMouseLeavePrice,
    onClickPrice,
  }: BidSideProps) {
    const {
      bidDisplay,
      bidPrice,
      showOfferTotal,
      offerTotal,
      homeCurrency,
      size,
      minimumSize,
      sizeSpread,
      isReadOnly,
      isActive,
    } = cell;

    const homeCurrencyInfo = useHomeCurrency();
    const { getOrderCurrency } = useGetOrderCurrency();
    const handleMouseEnter = useCallback(() => {
      if (bidDisplay) {
        onMouseHoverPrice(ORDER_SIDES.SELL);
      }
    }, [bidDisplay, onMouseHoverPrice]);

    const handleClick = useCallback(
      (e: MouseEvent, intent: OrderIntent) => {
        e.preventDefault();
        if (!isReadOnly && bidPrice != null) {
          const side = intent === AggressorFlagEnum.Active ? SideEnum.Sell : SideEnum.Buy;
          const currency = getOrderCurrency(security, side);
          onClickPrice(
            {
              side,
              currency,
              orderQty: size,
              price: bidPrice,
              intent,
            },
            index
          );
        }
      },
      [bidPrice, index, isReadOnly, onClickPrice, size, security, getOrderCurrency]
    );

    return (
      <BidsSideCell
        isReadOnly={bidPrice == null}
        isActive={isActive}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={onMouseLeavePrice}
        onClick={e => handleClick(e, AggressorFlagEnum.Active)}
        onContextMenu={e => handleClick(e, AggressorFlagEnum.Passive)}
        data-testid={`volume-ladder-bid-${index}`}
      >
        <LadderBg
          isBid
          style={{
            transform: sizeSpread == null ? 'scaleX(0)' : `scaleX(${sizeSpread})`,
          }}
        />
        {showOfferTotal ? (
          <TotalPopup>
            <InlineFormattedNumber
              number={offerTotal}
              currency={homeCurrency}
              increment={homeCurrencyInfo?.DefaultIncrement}
              round
              estimate
            />
          </TotalPopup>
        ) : (
          <>
            <BidBucket
              increment={security.MinSizeIncrement}
              min={minimumSize}
              index={index}
              rawValue={size}
              isReadOnly={isActive}
              onUpdateSize={onUpdateSize}
              precision={0}
              popOverPrecision={2}
              popOverPlacement="left"
            />
            <Price>
              <InlineFormattedNumber
                isSmall
                highlightAll={false}
                largeHighlight={true}
                number={bidDisplay}
                specification={security.PriceDisplaySpec}
              />
            </Price>
          </>
        )}
      </BidsSideCell>
    );
  },
  (p, n) => isEqual(p, n)
);

interface OfferCell {
  offerDisplay?: string;
  offerPrice?: string;
  showBidTotal: boolean | string;
  bidTotal: string;
  homeCurrency: string;
  size: string;
  minimumSize: string;
  sizeSpread: number;
  isReadOnly: boolean;
  isActive: boolean;
}

interface OfferSideProps {
  index: number;
  security: Security;
  cell: OfferCell;
  onUpdateSize: (index: number, newSize: string) => void;
  onMouseHoverPrice: (side: string) => void;
  onMouseLeavePrice: () => void;
  onClickPrice: (args: IOnClickPriceArgs, index: number) => any;
}

const OfferSide = memo(
  function OfferSide({
    index,
    security,
    cell,
    onUpdateSize,
    onMouseHoverPrice,
    onMouseLeavePrice,
    onClickPrice,
  }: OfferSideProps) {
    const {
      offerDisplay,
      offerPrice,
      showBidTotal,
      bidTotal,
      homeCurrency,
      size,
      minimumSize,
      sizeSpread,
      isReadOnly,
      isActive,
    } = cell;

    const homeCurrencyInfo = useHomeCurrency();
    const { getOrderCurrency } = useGetOrderCurrency();

    const handleMouseEnter = useCallback(() => {
      if (offerDisplay) {
        onMouseHoverPrice(ORDER_SIDES.BUY);
      }
    }, [offerDisplay, onMouseHoverPrice]);

    const handleClick = useCallback(
      (e: MouseEvent, intent: OrderIntent) => {
        e.preventDefault();
        if (!isReadOnly && offerPrice != null) {
          const side = intent === AggressorFlagEnum.Active ? SideEnum.Buy : SideEnum.Sell;
          const currency = getOrderCurrency(security, side);
          onClickPrice(
            {
              side,
              currency,
              orderQty: size,
              price: offerPrice,
              intent,
            },
            index
          );
        }
      },
      [offerPrice, index, isReadOnly, onClickPrice, size, security, getOrderCurrency]
    );

    return (
      <OffersSideCell
        isReadOnly={offerPrice == null}
        isActive={isActive}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={onMouseLeavePrice}
        onClick={e => handleClick(e, AggressorFlagEnum.Active)}
        onContextMenu={e => handleClick(e, AggressorFlagEnum.Passive)}
        data-testid={`volume-ladder-offer-${index}`}
      >
        <LadderBg
          isOffer
          style={{
            transform: sizeSpread == null ? 'scaleX(0)' : `scaleX(${sizeSpread})`,
          }}
        />
        {showBidTotal ? (
          <TotalPopup>
            <InlineFormattedNumber
              number={bidTotal}
              currency={homeCurrency}
              increment={homeCurrencyInfo?.DefaultIncrement}
              round
              estimate
            />
          </TotalPopup>
        ) : (
          <>
            <Price>
              <InlineFormattedNumber
                isSmall
                highlightAll={false}
                largeHighlight={true}
                number={offerDisplay}
                specification={security.PriceDisplaySpec}
              />
            </Price>
            <OfferBucket
              increment={security.MinSizeIncrement}
              min={minimumSize}
              index={index}
              rawValue={size}
              isReadOnly={isActive}
              onUpdateSize={onUpdateSize}
              isRightAligned
              precision={0}
              popOverPrecision={2}
              popOverPlacement="right"
            />
          </>
        )}
      </OffersSideCell>
    );
  },
  (p, n) => isEqual(p, n)
);
